import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from "react";
import {
  faSort,
  faSortUp,
  faSortDown,
  faTimes,
  // faEdit,
  faRefresh,
} from "@fortawesome/free-solid-svg-icons";
import { AccountDataService } from "../../services/AccountDataService";
import useSWR from "swr";
import { cancelAllOrdersBySymbol, cancelOrderByOrderId, cancelOcoOrderByOrderId, cancelStopOrderByOrderId, getAllOpenOrdersForSymbol, getOpenOrdersByPage } from "../../../../services/order.service";
import { connectSocket, CancelOrderData } from "../../../../services/websocket-client.service";
import { getCexEngineSocket } from "../../../../services/cex-engine-socket.service";
import { getUserOpenOrders } from "../../../../services/cex-engine.service";
import { OrderDetails, OrderResponse, SortConfig, SortField } from "../../../../types/order.types";
import { formatDate, formatNumber } from "../../../../utils/helper";
import { useSnackbarMsg } from "../../../../hooks/snackbar";
import { useOrdersData } from "../../../../recoil/hooks/userOrderState";
import { useCurrentPrice, useDarkMode, useIsExRoute } from "../../../../recoil";

interface OpenOrderProps {
  market: string;
  isActive: boolean;
  setOrderLength: React.Dispatch<React.SetStateAction<number>>;
  setIsCancellingAll: React.Dispatch<React.SetStateAction<boolean>>;
  isUserAuthenticated: boolean;
  hideData: boolean;
}

export interface OpenOrderRef {
  handleCancelAllSymbolOrders: () => void;
  mutateOpenOrders: () => void;
}

const OpenOrder = forwardRef<OpenOrderRef, OpenOrderProps>((props, ref) => {
  const [currentPrice] = useCurrentPrice();
  const [darkMode, setDarkMode] = useDarkMode();
  const [currentOpenOrders, setCurrentOpenOrders] = useOrdersData();
  const [msg, setMsg] = useSnackbarMsg();
  const { market, isActive, setOrderLength, setIsCancellingAll, isUserAuthenticated, hideData } = props;
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: "date",
    direction: "desc",
  });
  const [openOrders, setOpenOrders] = useState<OrderDetails[]>([]);
  const [isCancelling, setIsCancelling] = useState<string | null>(null);

  // Pagination state - same as OrderHistory
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5; // Show 5 rows per page

  const wsRef = useRef<WebSocket | null>(null);
  const serviceRef = useRef<AccountDataService | null>(null);
  const socketRef = useRef<any>(null);
  const isExotic = useIsExRoute();

  // --- SWR fetch ---
  const swrKey = market && isUserAuthenticated ? [isExotic ? "getUserOpenOrders" : "getAllOpenOrdersForSymbol", market, isExotic] : null;
  // Explicitly type the fetcher to return either an array of OrderResponse or the OpenOrdersResponse shape (any used for the latter)
  const swrFetcher = async (): Promise<OrderResponse[] | any> =>
    isExotic
      ? await getUserOpenOrders({ symbol: market, type: 'limit' })
      : await getAllOpenOrdersForSymbol(market);

  const { data: openOrdersResponse, error, mutate: mutateOpenOrders } = useSWR<OrderResponse[] | any>(swrKey, swrFetcher, {
    errorRetryInterval: 3000,
    errorRetryCount: 0,
    revalidateOnFocus: false,
  });


  // Use response data
  useEffect(() => {
    const orders = openOrdersResponse ? (isExotic ? (openOrdersResponse as OrderResponse[]) : (openOrdersResponse as any)?.items || []) : null;
    if (orders) {
      const filteredOpenOrders = orders.filter((order: OrderResponse) => (["open", "OPEN"]?.includes(order.status)) && order.type === 'limit' && order.symbol === market);
      setOpenOrders(filteredOpenOrders);
      setOrderLength(filteredOpenOrders.length || 0);
    }
  }, [openOrdersResponse, isExotic]);

  const isLoading = !openOrdersResponse && !error;

  useEffect(() => {
    setCurrentOpenOrders(openOrders || []);
  }, [openOrders]);


  // --- WebSocket updates ---
  const normalizeWsOrder = (wsOrder: any): OrderDetails => ({
    id: wsOrder.orderId,
    orderId: wsOrder.orderId, // Added property
    status: wsOrder.status || "open", // Added property
    clientOid: wsOrder.clientOid || "",
    symbol: wsOrder.symbol,
    opType: wsOrder.orderType || wsOrder.type || "",
    type: wsOrder.orderType || wsOrder.type || "",
    side: wsOrder.side,
    price: wsOrder.price,
    size: wsOrder.size || wsOrder.originSize,
    funds: wsOrder.funds || wsOrder.price * (parseFloat(wsOrder.size) || 0) + "",
    dealSize: wsOrder.filledSize || "0",
    dealFunds: wsOrder.dealFunds || "0",
    fee: wsOrder.fee || "0",
    feeCurrency: wsOrder.feeAsset || "",
    stp: wsOrder.stp || null,
    timeInForce: wsOrder.timeInForce || "GTC",
    postOnly: wsOrder.postOnly || false,
    hidden: wsOrder.hidden || false,
    iceberg: wsOrder.iceberg || false,
    visibleSize: wsOrder.visibleSize || "0",
    cancelAfter: wsOrder.cancelAfter || 0,
    channel: "websocket",
    remark: wsOrder.remark || "",
    tags: wsOrder.tags || null,
    cancelExist: false,
    createdAt: wsOrder.orderTime || wsOrder.pt || Date.now(),
    lastUpdatedAt: wsOrder.pt || wsOrder.ts || Date.now(),
    tradeType: wsOrder.tradeType || "TRADE",
    inOrderBook: true,
    cancelledSize: wsOrder.canceledSize || "0",
    cancelledFunds: wsOrder.cancelledFunds || "0",
    remainSize: wsOrder.remainSize || wsOrder.remainingQuantity || "0",
    remainFunds: wsOrder.remainFunds || "0",
    tax: "0",
    cancelReason: 0,
    active: wsOrder.status === "open",
    quantity: wsOrder.quantity || wsOrder.size || "0",
  });

  useEffect(() => {
    serviceRef.current = new AccountDataService();

    const connectWS = async () => {
      if (!serviceRef.current) return;

      wsRef.current = await serviceRef.current.connectOpenOrdersWebSocket(
        (orderUpdate) => {
          let updatedOrders = Array.isArray(orderUpdate) ? orderUpdate : [orderUpdate];
          updatedOrders = updatedOrders?.filter((o) => o.symbol === market && o?.orderType == 'limit') || [];
          setOpenOrders((prevOrders) => {
            const map = new Map(prevOrders.map((o) => [o.id, o]));
            updatedOrders.forEach((o) => {
              const normalized = normalizeWsOrder(o);
              map.set(normalized.id, {...map.get(normalized.id) , ...normalized});
            });
            let newOrders = Array.from(map.values());
            newOrders = newOrders.filter((order) => order.status === "open");
            setOrderLength(newOrders.length || 0);
            return newOrders;
          });
        }
      );
    };

    connectWS();

    // Set up websocket for orders
    if (isUserAuthenticated) {
      socketRef.current = isExotic ? getCexEngineSocket() : connectSocket();
      if (isExotic && socketRef.current) {
        getCexEngineSocket()?.onOrderAdded((data) => {
          console.log(data)
          setMsg({
            msg: `Order placed successfully!`,
            type: 'success'
          });
          mutateOpenOrders();
        });

        getCexEngineSocket()?.onOrderCanceled((data) => {
          setMsg({
            msg: "Order cancelled successfully",
            type: "success"
          })
          mutateOpenOrders();
        });
      }
    }

    return () => {
      if (wsRef.current) wsRef.current.close();
      // Note: Don't disconnect the shared socket here
    };
  }, [isUserAuthenticated, isExotic]);

  const handleSort = (field: SortField) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIcon = (field: SortField) => {
    if (sortConfig.field !== field) return faSort;
    return sortConfig.direction === "asc" ? faSortUp : faSortDown;
  };

  const handleCancelOrder = async (orderId: string, isOcoOrder: boolean, isStopOrder: boolean) => {
    try {
      setIsCancelling(orderId);

      // Priority: OCO > Stop > Regular orders
      if (isOcoOrder) {
        // Cancel OCO order using REST API
        await cancelOcoOrderByOrderId(orderId);
      } else if (isStopOrder) {
        // Cancel Stop order using REST API
        await cancelStopOrderByOrderId(orderId);
      } else {
        // Cancel regular order - use appropriate socket based on isExotic
        const currentSocket = socketRef.current;
        if (!currentSocket?.cancelOrder) {
          // Fallback to REST API if websocket not available
          await cancelOrderByOrderId(orderId);
        } else {
          const cancelData = { orderId, symbol: market };
          const response = await currentSocket.cancelOrder(cancelData);
          console.log("after cancel order by socket", response);
          if (!response.success && !isExotic) {
            throw new Error(response.error || 'Cancel order failed');
          }
        }
      }
      console.log("after cancel order");
      // Refresh open orders after cancellation
      await mutateOpenOrders();
      if (!isExotic)
        setMsg({
          msg: "Order cancelled successfully",
          type: 'success'
        });
    } catch (error) {
      console.error('Error cancelling order:', error);
      setMsg({
        msg: `Error cancelling order: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error'
      });
    } finally {
      setIsCancelling(null);
    }
  };

  // --- Apply sorting ---
  const processedOpenOrders = useMemo(() => {
    const sorted = [...openOrders].sort((a, b) => {
      const { field, direction } = sortConfig;
      let compare = 0;
      switch (field) {
        case "date":
          compare = a.createdAt - b.createdAt;
          break;
        case "price":
          compare = parseFloat(a.price) - parseFloat(b.price);
          break;
        case "amount":
          compare = parseFloat(a.size) - parseFloat(b.size);
          break;
        case "total":
          compare = parseFloat(a.funds) - parseFloat(b.funds);
          break;
        case "side":
          compare = a.side.localeCompare(b.side);
          break;
        default:
          compare = 0;
      }
      return direction === "asc" ? compare : -compare;
    });
    return sorted;
  }, [openOrders, sortConfig]);

  // --- Pagination Calculation - Same as OrderHistory ---
  const totalItems = processedOpenOrders.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageData = processedOpenOrders.slice(startIndex, endIndex);

  const handleCancelAllOrders = async () => {
    if (!market) return;
    try {
      setIsCancellingAll(true);
      await cancelAllOrdersBySymbol(market);
      setOpenOrders([]);
      setMsg({
        msg: "All orders cancelled successfully",
        type: 'success'
      });
    } catch (error) {
      console.error('Error cancelling all orders:', error);
      setMsg({
        msg: "Failed to cancel all orders. Please try again.",
        type: 'error'
      });
    } finally {
      setIsCancellingAll(false);
    }
  };

  // Imperative methods exposed to parent
  useImperativeHandle(ref, () => ({
    handleCancelAllSymbolOrders: () => {
      handleCancelAllOrders();
    },
    mutateOpenOrders: () => mutateOpenOrders(),
  }), [openOrders]);

  if (!isActive) return null;

  return (
    <div className={`h-full  ${darkMode ? 'custom_table_container' : 'custom_table_container_light'} text-[#121212] dark:!text-white`}>
      <div className="table-responsive">
        <table className={`table  custom_table table-hover ${darkMode ? 'table-dark' : 'table-light'}`}>
          <thead>
            <tr>
              <th className="cursor-pointer" onClick={() => handleSort("date")}>
                Date <FontAwesomeIcon icon={getSortIcon("date")} className="ml-1" />
              </th>
              <th>Pair</th>
              <th>Order Type</th>
              <th className="cursor-pointer" onClick={() => handleSort("side")}>
                Side <FontAwesomeIcon icon={getSortIcon("side")} className="ml-1" />
              </th>
              <th className="cursor-pointer" onClick={() => handleSort("price")}>
                Price <FontAwesomeIcon icon={getSortIcon("price")} className="ml-1" />
              </th>
              <th className="cursor-pointer" onClick={() => handleSort("amount")}>
                Amount <FontAwesomeIcon icon={getSortIcon("amount")} className="ml-1" />
              </th>
              <th className="cursor-pointer" onClick={() => handleSort("total")}>
                Total <FontAwesomeIcon icon={getSortIcon("total")} className="ml-1" />
              </th>
              <th>Time In Force</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={9} className="text-center text-gray-400 py-4">
                  Loading...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={9} className="text-center text-red-400 py-4">
                  Error loading orders. Retrying...
                </td>
              </tr>
            ) : currentPageData.length > 0 ? (
              currentPageData.map((order) => (
                <tr key={order.id} className="">
                  <td>{formatDate(order.createdAt)}</td>
                  <td className="flex justify-start items-center"> <img
                    src={`https://assets.coincap.io/assets/icons/${(
                      order?.symbol?.split('-')[1] ||
                      order?.symbol?.split('/')[0] || // Handle BTC/USDT format
                      'btc'
                    ).toLowerCase()}@2x.png`}
                    alt=""
                    className="mr-2 h-[20px] w-[20px] rounded-full object-cover"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = 'https://via.placeholder.com/20?text=C'; // Fallback to a placeholder
                      e.currentTarget.style.display = 'block'; // Ensure it's visible
                    }}
                  />{order.symbol}</td>
                  <td>{order?.type?.toUpperCase()}</td>
                  <td
                    className={order.side === "buy" ? " text-up" : "text-down"}
                  >
                    {order.side.toUpperCase()}
                  </td>
                  <td>
                    {
                      hideData ? '****' : <div>
                        <div>{formatNumber(order.price)}</div>

                        {/* current price and percent difference */}
                        <div className="text-xs mt-1 flex items-center space-x-2">
                          {(() => {
                            try {
                              if (currentPrice?.marketParam !== market) return null; // ensure price corresponds to current market
                              const curVal = currentPrice?.value ?? null;
                              const orderVal = order?.price ?? order?.quantity ?? null;
                              const cur = curVal !== null ? parseFloat(String(curVal)) : NaN;
                              const ord = orderVal !== null ? parseFloat(String(orderVal)) : NaN;

                              if (!isFinite(cur) || !isFinite(ord)) {
                                return null;
                              }

                              const diff = ((ord - cur) / cur) * 100;
                              const diffSign = diff > 0 ? "+" : diff < 0 ? "-" : "";

                              return (
                                <>
                                  <span className={`${diff > 0 ? "text-up" : diff < 0 ? "text-down" : "text-gray-400"}`}>
                                    {diffSign}&nbsp;
                                    {Math.abs(diff).toFixed(2)}%
                                  </span>
                                </>
                              );
                            } catch (e) {
                              // graceful fallback on any unexpected error
                              return null;
                            }
                          })()}
                        </div>
                      </div>
                    }
                  </td>
                  <td> {hideData ? '****' : <>{formatNumber(order?.size?.toString() || order?.quantity?.toString() || "")}</>} </td>
                  <td>{hideData ? '****' : formatNumber(order.funds)}</td>
                  <td>{order.timeInForce}</td>
                  <td>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleCancelOrder(order.orderId || order?.id, order?.isOcoOrder || false, order?.isStopOrder || false)}
                        disabled={isCancelling === (order.orderId || order?.id)}
                        className="text-red text-xs px-2 py-1 rounded disabled:opacity-50"
                        title="Cancel Order"
                      >
                        {isCancelling === (order.orderId || order?.id) ? (
                          <FontAwesomeIcon icon={faRefresh} className="animate-spin" />
                        ) : (
                          <FontAwesomeIcon icon={faTimes} />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="text-center hover:bg-white text-gray-400 py-4">
                  No open orders found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* --- Pagination Controls - Same as OrderHistory --- */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-4 px-4">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-gray-700 text-white rounded disabled:opacity-50 hover:bg-gray-600 transition-colors"
          >
            Previous
          </button>
          <span className="text-sm text-gray-300">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => p + 1)}
            disabled={currentPage >= totalPages}
            className="px-3 py-1 bg-gray-700 text-white rounded disabled:opacity-50 hover:bg-gray-600 transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
});

export default OpenOrder;
